import React, { useState } from "react";

import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableHead from "@app/analytics/components/Table/TableHead";
import useAuth from "@app/common/auth/useAuth";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import {
  EnterpriseRole,
  EnterpriseUser,
  EnterpriseWorkspaceInvite,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { removeAt } from "@app/utils/arrays";

import MembersTableInviteRow from "./MembersTableInviteRow";
import MembersTableUserRow from "./MembersTableUserRow";
import TeamPermissionsModal from "../../modals/TeamPermissionsModal";
import SelectedSnackbar from "../SelectedSnackbar";

export type MembersContext = "team" | "workspace";

interface MembersTableProps {
  context: MembersContext;
  headCells: HeadCell<EnterpriseUser | EnterpriseWorkspaceInvite>[];
  users: EnterpriseUser[];
  invites: EnterpriseWorkspaceInvite[];
  onMemberRemove: (id: string, type: "user" | "invite") => () => void;
  onRemoveMultiple: (
    rows: { id: string; type: "user" | "invite" }[],
    onSuccess: (failures: string[]) => void
  ) => void;
  onRoleChange: (userId: string) => (role: EnterpriseRole) => void;
  canEditTeam: boolean;
}

const MembersTable = ({
  context,
  headCells,
  invites,
  onMemberRemove,
  onRemoveMultiple,
  onRoleChange,
  users,
  canEditTeam,
}: MembersTableProps) => {
  const { user } = useAuth();
  const { workspaceIsInactive, workspace } = useWorkspace();
  const [teamPermissionsModalOpen, setTeamPermissionsModalOpen] =
    useState<boolean>(false);
  const [memberIdToUpdate, setMemberIdToUpdate] = useState<string | null>(null);

  const [selectedRows, setSelectedRows] = useState<
    { id: string; type: "user" | "invite" }[]
  >([]);

  const usersAndInvites = canEditTeam ? [...users, ...invites] : [...users];

  const { handleSort, order, orderBy, sortedData } = useDataTable(
    usersAndInvites,
    headCells,
    {
      defaultOrder: "asc",
    }
  );

  const handleToggleAll = () => {
    if (context === "workspace") {
      const allChecked = selectedRows.length === sortedData.length;
      if (allChecked) {
        setSelectedRows([]);
      } else {
        setSelectedRows(
          sortedData.map((userOrInvite) => ({
            id:
              userOrInvite.type === "invite"
                ? userOrInvite.id
                : userOrInvite.userId,
            type: userOrInvite.type,
          }))
        );
      }
    } else {
      // workspace invites can't be removed from the teams context
      const allChecked = selectedRows.length === users.length;
      if (allChecked) {
        setSelectedRows([]);
      } else {
        setSelectedRows(
          users.map((user) => ({ id: user.userId, type: user.type }))
        );
      }
    }
  };

  const toggleRow = (id: string, type: "user" | "invite") => () => {
    const currentIndex = selectedRows.findIndex((row) => row.id === id);
    if (currentIndex < 0) {
      setSelectedRows([...selectedRows, { id, type }]);
    } else {
      setSelectedRows(removeAt(selectedRows, currentIndex));
    }
  };

  const toggleTeamPermissionsModal = (userId: string | null) => () => {
    setMemberIdToUpdate(userId);
    setTeamPermissionsModalOpen(Boolean(userId));
  };

  const handleRemoveMultiple = async () =>
    onRemoveMultiple(selectedRows, (failedIds) =>
      setSelectedRows(selectedRows.filter(({ id }) => failedIds.includes(id)))
    );

  return (
    <>
      {canEditTeam && (
        <SelectedSnackbar
          numSelected={selectedRows.length}
          onRemove={handleRemoveMultiple}
          onUnselect={() => setSelectedRows([])}
        />
      )}
      <Table>
        <TableHead
          headCells={headCells}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
          checkboxProps={
            canEditTeam
              ? {
                  numSelected: selectedRows.length,
                  rowCount:
                    context === "workspace" ? sortedData.length : users.length,
                  onSelectAllClick: handleToggleAll,
                }
              : undefined
          }
        />
        <TableBody>
          {sortedData.map(
            (userOrInvite: EnterpriseUser | EnterpriseWorkspaceInvite) => {
              const isInvite = userOrInvite.type === "invite";

              if (isInvite) {
                const selectedRowOption = selectedRows.find(
                  (row) => row.id === userOrInvite.id
                );
                return (
                  <MembersTableInviteRow
                    key={userOrInvite.id}
                    context={context}
                    invite={userOrInvite}
                    isSelected={Boolean(selectedRowOption)}
                    onCheckboxClick={toggleRow}
                    onMemberRemove={onMemberRemove}
                  />
                );
              }

              const selectedRowOption = selectedRows.find(
                (row) => row.id === userOrInvite.userId
              );
              return (
                <MembersTableUserRow
                  key={userOrInvite.userId}
                  canEditTeam={canEditTeam}
                  context={context}
                  currentUser={user!}
                  forceMenuClose={Boolean(memberIdToUpdate)}
                  isSelected={Boolean(selectedRowOption)}
                  onCheckboxClick={toggleRow}
                  onMemberRemove={onMemberRemove}
                  onTeamPermissionsClick={toggleTeamPermissionsModal}
                  onRoleChange={onRoleChange}
                  user={userOrInvite}
                  workspace={workspace}
                  workspaceIsInactive={workspaceIsInactive}
                />
              );
            }
          )}
          <TeamPermissionsModal
            userId={memberIdToUpdate!}
            open={teamPermissionsModalOpen}
            onClose={toggleTeamPermissionsModal(null)}
          />
        </TableBody>
      </Table>
    </>
  );
};

export default MembersTable;
